/**
 * RSSI 값을 사용자 친화적인 텍스트와 아이콘으로 변환하는 유틸리티
 */

/**
 * RSSI 값을 신호 강도 정보로 변환
 * @param {number} rssi - RSSI 값 (dBm, 일반적으로 -100 ~ 0 범위)
 * @returns {Object} 신호 강도 정보 객체
 *   - label: 표시할 텍스트 (예: "매우 좋음")
 *   - icon: FontAwesome 아이콘 클래스
 *   - color: 색상 코드
 *   - level: 신호 강도 레벨 (1-5)
 */
export function getSignalStrength(rssi) {
  // rssi가 유효하지 않은 경우
  if (rssi === null || rssi === undefined || isNaN(rssi)) {
    return {
      label: '정보 없음',
      icon: 'fa-wifi',
      color: '#6c757d',
      level: 0,
    };
  }

  // RSSI 값이 높을수록 신호가 좋음 (0에 가까울수록 좋음)
  if (rssi >= -40) {
    // 매우 좋음: -40 이상
    return {
      label: '매우 좋음',
      icon: 'fa-wifi',
      color: '#28a745',
      level: 5,
    };
  } else if (rssi >= -50) {
    // 좋음: -50 이상 ~ -40 미만
    return {
      label: '좋음',
      icon: 'fa-wifi',
      color: '#7cb342',
      level: 4,
    };
  } else if (rssi >= -60) {
    // 보통: -60 이상 ~ -50 미만
    return {
      label: '보통',
      icon: 'fa-wifi',
      color: '#ffc107',
      level: 3,
    };
  } else if (rssi >= -70) {
    // 약함: -70 이상 ~ -60 미만
    return {
      label: '약함',
      icon: 'fa-wifi',
      color: '#ff9800',
      level: 2,
    };
  } else {
    // 매우 약함: -70 미만
    return {
      label: '매우 약함',
      icon: 'fa-wifi',
      color: '#dc3545',
      level: 1,
    };
  }
}

/**
 * RSSI 값을 막대 개수(0-10)로 변환
 * @param {number} rssi - RSSI 값
 * @returns {number} 막대 개수 (0-10)
 */
export function getSignalBars(rssi) {
  if (rssi === null || rssi === undefined || isNaN(rssi)) {
    return 0;
  }

  // RSSI 범위: -100 ~ 0 (일반적으로)
  // -40 이상: 10개
  // -45 이상 ~ -40 미만: 9개
  // -50 이상 ~ -45 미만: 8개
  // -55 이상 ~ -50 미만: 7개
  // -60 이상 ~ -55 미만: 6개
  // -65 이상 ~ -60 미만: 5개
  // -70 이상 ~ -65 미만: 4개
  // -75 이상 ~ -70 미만: 3개
  // -80 이상 ~ -75 미만: 2개
  // -80 미만: 1개
  
  if (rssi >= -40) {
    return 10;
  } else if (rssi >= -45) {
    return 9;
  } else if (rssi >= -50) {
    return 8;
  } else if (rssi >= -55) {
    return 7;
  } else if (rssi >= -60) {
    return 6;
  } else if (rssi >= -65) {
    return 5;
  } else if (rssi >= -70) {
    return 4;
  } else if (rssi >= -75) {
    return 3;
  } else if (rssi >= -80) {
    return 2;
  } else {
    return 1;
  }
}

/**
 * RSSI 값을 dBm 단위 문자열로 포맷팅
 * @param {number} rssi - RSSI 값
 * @returns {string} 포맷팅된 문자열 (예: "-51 dBm")
 */
export function formatRssi(rssi) {
  if (rssi === null || rssi === undefined || isNaN(rssi)) {
    return '정보 없음';
  }
  return `${rssi} dBm`;
}
